import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import {fetchProgramLayoutMock} from "../../../services/_mock_api/fetch_program_layout.mock";
import {fetchLearningProgramOutline} from "../../../services/learning_program.service";
import {useGlobalState} from "../../../context/global_state";
import { LOCAL_STORAGE_KEYS } from '../../../utils/constants';
import { GET_APP_LOCALIZE_TEXT } from "../../../utils/dq_lc_service/lc_service";
import { useLocation } from "react-router-dom";


// Animation for the loader
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// Styled components
const CoursePreviewContainer = styled.div`
  padding: 15px;
  font-family: inherit;
  text-align: left;
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

const Loader = styled.div`
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 4px solid ${props => props.themeColor || '#3498db'};
  width: 30px;
  height: 30px;
  animation: ${spin} 1s linear infinite;
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  font-family: "Source Sans Pro", "Helvetica Neue";
`;

const ExpandIcon = styled.span`
  font-size: 0.7rem;
  margin-right: 8px;
  color: ${props => props.expanded ? '#666' : '#888'};
  transition: transform 0.2s ease;
  transform: ${props => props.expanded ? 'rotate(0deg)' : 'rotate(0deg)'};
  cursor: pointer;
  user-select: none;
`;

const CourseTitle = styled.h3`
  color: ${props => props.themeColor || '#333'};
  font-weight: ${props => props.completed ? '800' : '700'};
  margin-bottom: ${props => props.expandable ? '15px' : '20px'};
  font-size: 1.1rem;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  opacity: ${props => props.completed ? 1 : 0.9};
  cursor: ${props => props.expandable ? 'pointer' : 'default'};
  user-select: none;
  font-family: "Source Sans Pro", "Helvetica Neue";

  &:hover {
    background-color: ${props => props.expandable ? 'rgba(0,0,0,0.02)' : 'transparent'};
  }
`;

const CourseDesc = styled.h5`
  color: ${props => props.themeColor || '#333'};
  font-weight: 500;
  margin-bottom: 20px;
  margin-left: 30px;
  margin-top: 10px;
  font-size: 0.9rem;
  text-align: left;
  display: flex;
  user-select: none;
  font-family: "Source Sans Pro", "Helvetica Neue";
`;

const ModuleContainer = styled.div`
  margin-bottom: 30px;
  text-align: left;
`;

const ModuleTitle = styled.div`
  font-weight: 600;
  color: ${props => props.themeColor || '#333'};
  margin-bottom: 10px;
  padding-bottom: 5px;
  border-bottom: 1px solid ${props => props.themeColor ? `${props.themeColor}40` : '#eee'};
  font-family: "Source Sans Pro", "Helvetica Neue";

`;

const UnitContainer = styled.div`
  margin-left: 25px;
  margin-bottom: 20px;
  text-align: left;
`;

const UnitTitle = styled.div`
  font-weight: ${props => props.completed ? '600' : '500'};
  margin-bottom: ${props => props.expandable ? '10px' : '12px'};
  font-size: 0.95rem;
  color: ${props => props.completed ? props.themeColor : (props.themeColor ? props.themeColor : '#444')};
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  opacity: ${props => props.completed ? 1 : 0.9};
  cursor: ${props => props.expandable ? 'pointer' : 'default'};
  user-select: none;
  font-family: "Source Sans Pro", "Helvetica Neue";


  &:hover {
    background-color: ${props => props.expandable ? 'rgba(0,0,0,0.02)' : 'transparent'};
  }
`;

const UnitDesc = styled.h5`
  color: ${props => props.themeColor || '#333'};
  font-weight: 500;
  margin-bottom: 20px;
  margin-left: 30px;
  margin-top: 10px;
  font-size: 0.9rem;
  text-align: left;
  display: flex;
  user-select: none;
  font-family: "Source Sans Pro", "Helvetica Neue";
`;

const SessionsList = styled.ul`
  list-style-type: none;
  padding-left: 25px;
  margin: 0;
  text-align: left;
  font-family: "Source Sans Pro", "Helvetica Neue";

`;

const CheckMark = styled.span`
  color: ${props => props.themeColor || 'green'};
  margin-left: 10px;
  font-weight: bold;
  font-size: 1.1em;
  display: inline-block;
  background-color: rgba(0, 128, 0, 0.1);
  border-radius: 50%;
  min-width: 22px;
  height: 22px;
  text-align: center;
  line-height: 20px;
`;

const SessionItem = styled.li`
  font-size: 1rem;
  margin-bottom: 8px;
  color: ${props => props.completed ? '#555' : '#000'};
  position: relative;
  text-align: left;
  opacity: ${props => props.completed ? 1 : 0.85};
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  font-family: "Source Sans Pro", "Helvetica Neue";

  &:before {
    content: "";
    position: absolute;
    left: -15px;
    top: 8px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: ${props => props.themeColor || '#777'};
  }
`;

const CoursePreview = ({ badge, pageLc: pageLcProp }) => {
  const [loading, setLoading] = useState(true);
  const [programLayout, setProgramLayout] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});
  const [expandedUnits, setExpandedUnits] = useState({});
  const { profile } = useGlobalState();
  const themeColor = badge?.theme_color || '#3498db';
  const location = useLocation();
  const pageLc = pageLcProp || location.pathname.split('/')[1];

  // Helper function to get profile from localStorage if not available
  const getProfileFromStorage = () => {
    try {
      const org_code = localStorage.getItem(LOCAL_STORAGE_KEYS.ACTIVE_ORG_CODE);
      if (org_code) {
        const available_profiles = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.AVAILABLE_PROFILES));
        if (available_profiles) {
          return available_profiles.find(profile => profile.org_code === org_code);
        }
      }
    } catch (error) {
      console.error('Error loading profile from localStorage:', error);
    }
    return null;
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        // Get profile - either from props or localStorage
        let currentProfile = profile;
        if (!currentProfile?._id) {
          currentProfile = getProfileFromStorage();
        }

        // If still no profile with _id, wait for it to be loaded
        if (!currentProfile?._id || !badge?.badge_code) {
          console.log('Waiting for profile or badge to be loaded...', { profile: currentProfile, badge: badge?.badge_code });
          return;
        }

        // const response = await fetchProgramLayoutMock();
        const response = await fetchLearningProgramOutline(badge.badge_code, currentProfile._id);
        setProgramLayout(response.data.program_layout);

        // Initialize all modules as expanded
        const moduleStates = {};
        Object.keys(response.data.program_layout).forEach(moduleCode => {
          moduleStates[moduleCode] = true;
        });
        setExpandedModules(moduleStates);

        // Initialize all units as expanded
        const unitStates = {};
        Object.values(response.data.program_layout).forEach(module => {
          module.units.forEach(unit => {
            unitStates[unit.unit_no] = false;
          });
        });
        setExpandedUnits(unitStates);

        setLoading(false);
      } catch (error) {
        console.error("Error fetching program layout:", error);
        setLoading(false);
      }
    };

    loadData();
  }, [badge?.badge_code, profile?._id, profile]);

  const toggleModule = (moduleCode) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleCode]: !prev[moduleCode]
    }));
  };

  const toggleUnit = (unitNo, e) => {
    e.stopPropagation(); // Prevent triggering the module toggle
    setExpandedUnits(prev => ({
      ...prev,
      [unitNo]: !prev[unitNo]
    }));
  };

  // Helper function to check if all sessions in a unit are completed
  const isUnitCompleted = (unit) => {
    return unit.sessions.length > 0 && unit.sessions.every(session => session.completed);
  };

  // Helper function to check if all units in a module are completed
  const isModuleCompleted = (module) => {
    return module.units.length > 0 && module.units.every(unit => isUnitCompleted(unit));
  };

  if (loading) {
    return (
        <LoaderContainer>
          <Loader themeColor={themeColor} />
        </LoaderContainer>
    );
  }

  return (
      <CoursePreviewContainer>
        {programLayout ? (
            <>
              {Object.values(programLayout).map((module, moduleIndex) => (
                  <ModuleContainer key={module.code}>
                    <CourseTitle
                        themeColor={themeColor}
                        completed={isModuleCompleted(module)}
                        onClick={() => toggleModule(module.code)}
                        expandable
                    >
                      <TitleWrapper>
                        <ExpandIcon expanded={expandedModules[module.code]}>
                          {expandedModules[module.code] ? '▼' : '►'}
                        </ExpandIcon>
                        <span>
                          {GET_APP_LOCALIZE_TEXT(pageLc, 'ct__module')} {moduleIndex + 1}: {module.full_name}
                        </span>
                      </TitleWrapper>
                      {isModuleCompleted(module) && <CheckMark themeColor={themeColor}>✓</CheckMark>}
                    </CourseTitle>

                    {expandedModules[module.code] && (
                        <>
                          <CourseDesc>{module.description}</CourseDesc>

                          {module.units.map((unit, unitIndex) => (
                              <UnitContainer key={unit.unit_no}>
                                <UnitTitle
                                    themeColor={themeColor}
                                    completed={isUnitCompleted(unit)}
                                    onClick={(e) => toggleUnit(unit.unit_no, e)}
                                    expandable
                                >
                                  <TitleWrapper>
                                    <ExpandIcon expanded={expandedUnits[unit.unit_no]}>
                                      {expandedUnits[unit.unit_no] ? '▼' : '►'}
                                    </ExpandIcon>
                                    <span>
                                      {GET_APP_LOCALIZE_TEXT(pageLc, 'ct__unit')} {unitIndex + 1}: {unit.full_name}
                                    </span>
                                  </TitleWrapper>
                                  {isUnitCompleted(unit) && <CheckMark themeColor={themeColor}>✓</CheckMark>}
                                </UnitTitle>

                                {expandedUnits[unit.unit_no] && (
                                    <>
                                      <UnitDesc>{unit.description}</UnitDesc>

                                      <SessionsList>
                                        {unit.sessions.map((session, sessionIndex) => (
                                            <SessionItem
                                                key={session.session_no}
                                                themeColor={themeColor}
                                                completed={session.completed}
                                            >
                                              <div>
                                                <span style={{fontWeight: '500',}}>
                                                  {GET_APP_LOCALIZE_TEXT(pageLc, 'ct__session')} {sessionIndex + 1}: {session.full_name}
                                                </span>
                                                <p style={{ margin: '4px 0 0 0', fontSize: '0.9em', marginLeft:'20px' }}>
                                                  {session.description}
                                                </p>
                                              </div>
                                              {session.completed && <CheckMark themeColor={themeColor}>✓</CheckMark>}
                                            </SessionItem>
                                        ))}
                                      </SessionsList>
                                    </>
                                )}
                              </UnitContainer>
                          ))}
                        </>
                    )}
                  </ModuleContainer>
              ))}
            </>
        ) : (
            <p>{GET_APP_LOCALIZE_TEXT(pageLc, 'ct__no_content_available')}</p>
        )}
      </CoursePreviewContainer>
  );
};

export default CoursePreview;